import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

type Tab = 'info' | 'password' | 'notifications' | 'verify' | 'account' | 'stats' | 'reviews' | 'blocked';

interface Region { id: number; name_ar: string; }
interface City { id: number; name_ar: string; }

const NOTIFY_FIELDS: { key: string; label: string }[] = [
  { key: 'notify_messages', label: 'إشعارات الرسائل والمحادثات' },
  { key: 'notify_reviews', label: 'إشعارات التقييمات' },
  { key: 'notify_ad_status', label: 'إشعارات حالة الإعلان' },
  { key: 'notify_promotions', label: 'إشعارات العروض والتمييز' },
  { key: 'notify_system', label: 'إشعارات النظام' },
];

const DOC_TYPES: { v: string; l: string }[] = [
  { v: 'national_id', l: 'الهوية الوطنية' },
  { v: 'commercial_register', l: 'السجل التجاري' },
  { v: 'freelance', l: 'وثيقة العمل الحر' },
];

const MyProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, updateUser, logout } = useAuth();
  const [tab, setTab] = useState<Tab>('info');
  const [statsData, setStatsData] = useState<any>(null);
  const [myReviews, setMyReviews] = useState<any[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<any[]>([]);
  const [tabLoading, setTabLoading] = useState(false);

  // البيانات الشخصية
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [bio, setBio] = useState('');
  const [publicShowPhone, setPublicShowPhone] = useState(false);
  const [publicShowEmail, setPublicShowEmail] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [regionId, setRegionId] = useState('');
  const [cityId, setCityId] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // كلمة المرور
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  // الإشعارات
  const [notify, setNotify] = useState<Record<string, boolean>>({});
  const [savingNotify, setSavingNotify] = useState(false);

  // التوثيق
  const [docType, setDocType] = useState('national_id');
  const [docFile, setDocFile] = useState<File | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyReq, setVerifyReq] = useState<{ status: string; rejection_reason: string | null; created_at: string; document_type: string } | null>(null);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setBio(user.bio || '');
      setPublicShowPhone(!!(user as any).public_show_phone);
      setPublicShowEmail(!!(user as any).public_show_email);
    }
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/regions').then((res) => setRegions(res.data?.data || [])).catch(() => {});
    api.get('/profile/notification-settings')
      .then((res) => {
        const s = res.data?.data || {};
        const map: Record<string, boolean> = {};
        NOTIFY_FIELDS.forEach((f) => { map[f.key] = s[f.key] !== undefined ? !!s[f.key] : true; });
        setNotify(map);
      })
      .catch(() => {});
    api.get('/profile')
      .then((res) => { const u = res.data?.data || {}; setPublicShowPhone(!!u.public_show_phone); setPublicShowEmail(!!u.public_show_email); })
      .catch(() => {});
    api.get('/profile/verification')
      .then((res) => setVerifyReq(res.data?.data?.request || null))
      .catch(() => {});
  }, [isAuthenticated]);

  const loadVerification = useCallback(() => {
    api.get('/profile/verification')
      .then((res) => setVerifyReq(res.data?.data?.request || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!regionId) { setCities([]); return; }
    api.get('/regions/' + regionId + '/cities').then((res) => setCities(res.data?.data || [])).catch(() => setCities([]));
  }, [regionId]);

  const saveInfo = useCallback(async () => {
    if (!name.trim()) { toast.error('الاسم مطلوب'); return; }
    try {
      setSavingInfo(true);
      const payload: any = { name: name.trim(), bio, public_show_phone: publicShowPhone, public_show_email: publicShowEmail };
      if (email.trim()) payload.email = email.trim();
      if (cityId) payload.city_id = cityId;
      const res = await api.put('/profile', payload);
      const updated = res.data?.data;
      if (updated && user) updateUser({ ...user, ...updated } as any);
      toast.success('تم حفظ البيانات');
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      toast.error(errors ? String(Object.values(errors)[0]) : (err?.response?.data?.message || 'تعذّر الحفظ'));
    } finally { setSavingInfo(false); }
  }, [name, email, bio, cityId, publicShowPhone, publicShowEmail, user, updateUser]);

  const onAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setAvatarUploading(true);
      const fd = new FormData();
      fd.append('avatar', file);
      const res = await api.post('/profile/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const avatar = res.data?.data?.avatar;
      if (avatar && user) updateUser({ ...user, avatar } as any);
      toast.success('تم تحديث الصورة');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر رفع الصورة');
    } finally { setAvatarUploading(false); }
  };

  const savePassword = async () => {
    if (!currentPw || !newPw) { toast.error('أكمل الحقول'); return; }
    if (newPw.length < 6) { toast.error('كلمة المرور 6 أحرف على الأقل'); return; }
    if (newPw !== confirmPw) { toast.error('كلمة المرور غير متطابقة'); return; }
    try {
      setSavingPw(true);
      await api.put('/auth/change-password', {
        current_password: currentPw,
        password: newPw,
        password_confirmation: confirmPw,
      });
      toast.success('تم تغيير كلمة المرور');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر تغيير كلمة المرور');
    } finally { setSavingPw(false); }
  };

  const saveNotify = async () => {
    try {
      setSavingNotify(true);
      await api.put('/profile/notification-settings', notify);
      toast.success('تم حفظ إعدادات الإشعارات');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر الحفظ');
    } finally { setSavingNotify(false); }
  };

  const submitVerify = async () => {
    if (!docFile) { toast.error('أرفق صورة الوثيقة'); return; }
    try {
      setVerifying(true);
      const fd = new FormData();
      fd.append('document_type', docType);
      fd.append('document_image', docFile);
      await api.post('/profile/verify', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم إرسال طلب التوثيق، سيتم مراجعته قريباً', { duration: 5000 });
      setDocFile(null);
      loadVerification();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر إرسال الطلب');
    } finally { setVerifying(false); }
  };

  const deactivate = async () => {
    if (!window.confirm('تعطيل حسابك؟ يمكنك تسجيل الدخول لاحقاً لإعادة تفعيله.')) return;
    try {
      await api.post('/profile/deactivate');
      toast.success('تم تعطيل الحساب');
      await logout();
      navigate('/login');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر التعطيل');
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm('حذف حسابك نهائياً؟ لا يمكن التراجع عن هذا الإجراء.')) return;
    if (!window.confirm('تأكيد أخير: سيتم حذف جميع بياناتك. متابعة؟')) return;
    try {
      await api.delete('/profile');
      toast.success('تم حذف الحساب');
      await logout();
      navigate('/');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر الحذف');
    }
  };

  useEffect(() => {
    if (tab === 'stats') {
      setTabLoading(true);
      api.get('/my/stats').then(r => setStatsData(r.data?.data || null)).catch(() => setStatsData(null)).finally(() => setTabLoading(false));
    } else if (tab === 'reviews') {
      setTabLoading(true);
      api.get('/my/reviews').then(r => setMyReviews(r.data?.data?.data || r.data?.data || [])).catch(() => setMyReviews([])).finally(() => setTabLoading(false));
    } else if (tab === 'blocked') {
      setTabLoading(true);
      api.get('/my/blocked-users').then(r => setBlockedUsers(r.data?.data?.data || r.data?.data || [])).catch(() => setBlockedUsers([])).finally(() => setTabLoading(false));
    }
  }, [tab]);

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: '80px 20px', fontFamily: "'Cairo', sans-serif", color: '#9E9E9E' }}>جاري التحميل...</div>;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'info', label: 'البيانات الشخصية', icon: '👤' },
    { key: 'password', label: 'كلمة المرور', icon: '🔒' },
    { key: 'notifications', label: 'الإشعارات', icon: '🔔' },
    { key: 'verify', label: 'التوثيق', icon: '✅' },
    ...((user.is_seller ? [
      { key: 'stats' as Tab, label: 'الإحصائيات', icon: '📊' },
      { key: 'reviews' as Tab, label: 'تقييماتي', icon: '⭐' },
    ] : [])),
    { key: 'blocked', label: 'المحظورون', icon: '🚫' },
    { key: 'account', label: 'الحساب', icon: '⚙️' },
  ];

  const input: React.CSSProperties = { width: '100%', height: 46, padding: '0 14px', border: '1.5px solid #E0E0E0', borderRadius: 10, fontFamily: "'Cairo',sans-serif", fontSize: 14, outline: 'none', background: '#fff' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, color: '#616161', marginBottom: 6, fontWeight: 600 };
  const btn: React.CSSProperties = { height: 46, padding: '0 24px', background: '#005343', color: '#fff', border: 'none', borderRadius: 10, fontFamily: "'Cairo',sans-serif", fontSize: 14, fontWeight: 700, cursor: 'pointer' };
  const outlineBtn: React.CSSProperties = { ...btn, background: '#fff', color: '#005343', border: '1.5px solid #005343', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'fit-content' };
  const isSeller = !!user.is_seller;
  const accountType = (user as any).account_type;

  return (
    <div style={{ maxWidth: 760, fontFamily: "'Cairo',sans-serif" }} dir="rtl">
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#212121', margin: '0 0 18px' }}>⚙️ إعدادات الحساب</h1>

      {/* التبويبات */}
      <div style={{ display: 'flex', gap: 6, borderBottom: '2px solid #E0E0E0', overflowX: 'auto', marginBottom: 20 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '11px 16px', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
            color: tab === t.key ? '#005343' : '#757575', background: 'none', border: 'none',
            borderBottom: '3px solid ' + (tab === t.key ? '#005343' : 'transparent'), marginBottom: -2,
            cursor: 'pointer', fontFamily: "'Cairo',sans-serif",
          }}>{t.icon} {t.label}</button>
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #E0E0E0', borderRadius: 14, padding: 24 }}>
        {tab === 'info' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingBottom: 6, borderBottom: '1px solid #F0F0F0' }}>
              <div style={{ position: 'relative' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#005343', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, overflow: 'hidden' }}>
                  {user.avatar ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (user.name?.charAt(0) || '؟')}
                </div>
                <label style={{ position: 'absolute', bottom: -2, left: -2, width: 24, height: 24, borderRadius: '50%', background: '#fff', border: '1px solid #E0E0E0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12 }} title="تغيير الصورة">
                  {avatarUploading ? '…' : '📷'}
                  <input type="file" accept="image/*" onChange={onAvatar} style={{ display: 'none' }} />
                </label>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>الصورة الشخصية</div>
                <div style={{ fontSize: 12, color: '#9E9E9E', marginTop: 2 }}>اضغط أيقونة الكاميرا لتغيير صورتك</div>
              </div>
            </div>
            <div><label style={labelStyle}>الاسم *</label><input style={input} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} /></div>
            <div><label style={labelStyle}>البريد الإلكتروني</label><input style={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" /></div>
            <div><label style={labelStyle}>نبذة</label><textarea style={{ ...input, height: 90, padding: 12, resize: 'none' }} value={bio} onChange={(e) => setBio(e.target.value)} maxLength={500} placeholder="نبذة قصيرة عنك..." /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>المنطقة</label>
                <select style={input} value={regionId} onChange={(e) => { setRegionId(e.target.value); setCityId(''); }}>
                  <option value="">اختر المنطقة</option>
                  {regions.map((r) => <option key={r.id} value={r.id}>{r.name_ar}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>المدينة</label>
                <select style={input} value={cityId} onChange={(e) => setCityId(e.target.value)} disabled={!cities.length}>
                  <option value="">{regionId ? 'اختر المدينة' : 'اختر المنطقة أولاً'}</option>
                  {cities.map((c) => <option key={c.id} value={c.id}>{c.name_ar}</option>)}
                </select>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #F0F0F0', paddingTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#616161', marginBottom: 8 }}>الخصوصية — ما يظهر في ملفك العام</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#424242', cursor: 'pointer', padding: '6px 0' }}>
                <input type="checkbox" checked={publicShowPhone} onChange={(e) => setPublicShowPhone(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#005343' }} /> إظهار رقم الجوال في ملفي العام
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#424242', cursor: 'pointer', padding: '6px 0' }}>
                <input type="checkbox" checked={publicShowEmail} onChange={(e) => setPublicShowEmail(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#005343' }} /> إظهار البريد الإلكتروني في ملفي العام
              </label>
            </div>
            <div><button style={{ ...btn, opacity: savingInfo ? 0.6 : 1 }} onClick={saveInfo} disabled={savingInfo}>{savingInfo ? 'جاري الحفظ...' : 'حفظ البيانات'}</button></div>
          </div>
        )}

        {tab === 'password' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 420 }}>
            <div><label style={labelStyle}>كلمة المرور الحالية *</label><input style={input} type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} dir="ltr" /></div>
            <div><label style={labelStyle}>كلمة المرور الجديدة *</label><input style={input} type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} dir="ltr" /></div>
            <div><label style={labelStyle}>تأكيد كلمة المرور *</label><input style={input} type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} dir="ltr" /></div>
            <div><button style={{ ...btn, opacity: savingPw ? 0.6 : 1 }} onClick={savePassword} disabled={savingPw}>{savingPw ? 'جاري الحفظ...' : 'تغيير كلمة المرور'}</button></div>
          </div>
        )}

        {tab === 'notifications' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {NOTIFY_FIELDS.map((f) => (
              <label key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 4px', borderBottom: '1px solid #F5F5F5', cursor: 'pointer' }}>
                <span style={{ fontSize: 14, color: '#424242' }}>{f.label}</span>
                <input type="checkbox" checked={!!notify[f.key]} onChange={(e) => setNotify((p) => ({ ...p, [f.key]: e.target.checked }))} style={{ width: 20, height: 20, accentColor: '#005343', cursor: 'pointer' }} />
              </label>
            ))}
            <div style={{ marginTop: 14 }}><button style={{ ...btn, opacity: savingNotify ? 0.6 : 1 }} onClick={saveNotify} disabled={savingNotify}>{savingNotify ? 'جاري الحفظ...' : 'حفظ الإعدادات'}</button></div>
          </div>
        )}

        {tab === 'verify' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 460 }}>
            {user.is_verified ? (
              <div style={{ background: '#E6F4EA', border: '1px solid #A5D6B7', borderRadius: 12, padding: 18, textAlign: 'center' }}>
                <span style={{ fontSize: 28 }}>✅</span>
                <p style={{ margin: '8px 0 0', fontWeight: 700, color: '#1E7A3C' }}>حسابك موثّق</p>
              </div>
            ) : verifyReq && verifyReq.status === 'pending' ? (
              <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 12, padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 22 }}>⏳</span>
                  <p style={{ margin: 0, fontWeight: 700, color: '#B8860B' }}>طلب التوثيق قيد المراجعة</p>
                </div>
                <p style={{ margin: '10px 0 0', fontSize: 13, color: '#8A6D00', lineHeight: 1.7 }}>
                  {verifyReq.created_at ? 'أرسلت طلبك بتاريخ ' + new Date(verifyReq.created_at).toLocaleDateString('en-GB') + '. ' : ''}
                  سيراجعه الفريق قريباً وسنُعلمك بالنتيجة عبر الإشعارات.
                </p>
              </div>
            ) : (
              <>
                {verifyReq && verifyReq.status === 'rejected' && (
                  <div style={{ background: '#FDECEA', border: '1px solid #F5B7B1', borderRadius: 12, padding: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 22 }}>❌</span>
                      <p style={{ margin: 0, fontWeight: 700, color: '#B91C1C' }}>تم رفض طلب التوثيق السابق</p>
                    </div>
                    {verifyReq.rejection_reason && (
                      <p style={{ margin: '10px 0 0', fontSize: 13, color: '#922B21', lineHeight: 1.7 }}>
                        <b>سبب الرفض:</b> {verifyReq.rejection_reason}
                      </p>
                    )}
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#922B21' }}>يمكنك تصحيح المستند وإعادة الإرسال أدناه.</p>
                  </div>
                )}
                {verifyReq && verifyReq.status === 'needs_changes' && (
                  <div style={{ background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: 12, padding: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 22 }}>✏️</span>
                      <p style={{ margin: 0, fontWeight: 700, color: '#1565C0' }}>الإدارة تطلب تعديلاً أو مستندًا إضافيًا</p>
                    </div>
                    {verifyReq.rejection_reason && (
                      <p style={{ margin: '10px 0 0', fontSize: 13, color: '#0D47A1', lineHeight: 1.7 }}>
                        <b>المطلوب:</b> {verifyReq.rejection_reason}
                      </p>
                    )}
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#0D47A1' }}>عدّل حسب الملاحظة وأعد الإرسال أدناه.</p>
                  </div>
                )}
                <p style={{ fontSize: 13, color: '#757575', margin: 0 }}>وثّق حسابك برفع مستند رسمي، وسيراجعه الفريق قبل التفعيل.</p>
                <div>
                  <label style={labelStyle}>نوع الوثيقة</label>
                  <select style={input} value={docType} onChange={(e) => setDocType(e.target.value)}>
                    {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>صورة الوثيقة</label>
                  <div style={{ border: '2px dashed #E0E0E0', borderRadius: 10, padding: 16, textAlign: 'center', cursor: 'pointer' }} onClick={() => document.getElementById('doc-input')?.click()}>
                    {docFile ? <span style={{ color: '#005343', fontWeight: 600, fontSize: 13 }}>📄 {docFile.name}</span> : <span style={{ color: '#9E9E9E', fontSize: 13 }}>اضغط لرفع الصورة (JPG/PNG)</span>}
                  </div>
                  <input id="doc-input" type="file" accept="image/*" onChange={(e) => setDocFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                </div>
                <div><button style={{ ...btn, opacity: verifying ? 0.6 : 1 }} onClick={submitVerify} disabled={verifying}>{verifying ? 'جاري الإرسال...' : (verifyReq && (verifyReq.status === 'rejected' || verifyReq.status === 'needs_changes') ? 'إعادة إرسال الطلب' : 'إرسال طلب التوثيق')}</button></div>
              </>
            )}
          </div>
        )}

        {tab === 'stats' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {tabLoading ? <p style={{ color: '#9E9E9E', textAlign: 'center', padding: 20 }}>جاري التحميل...</p> : statsData ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12 }}>
                  {[
                    { v: statsData.ads_total, l: 'إجمالي الإعلانات' },
                    { v: statsData.active, l: 'نشطة' },
                    { v: statsData.sold, l: 'مباعة' },
                    { v: statsData.views_total, l: 'المشاهدات' },
                    { v: statsData.favorites_total, l: 'المفضّلة' },
                    { v: statsData.messages_total, l: 'المحادثات' },
                  ].map((x, i) => (
                    <div key={i} style={{ background: '#F7F9F8', border: '1px solid #E6F2F0', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#005343' }}>{(x.v ?? 0).toLocaleString()}</div>
                      <div style={{ fontSize: 12, color: '#757575', fontWeight: 600, marginTop: 4 }}>{x.l}</div>
                    </div>
                  ))}
                </div>
                {Array.isArray(statsData.top_ads) && statsData.top_ads.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 800, color: '#212121', margin: '6px 0 10px' }}>الأكثر مشاهدة</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {statsData.top_ads.map((a: any) => (
                        <Link key={a.id} to={'/ads/' + a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fff', border: '1px solid #E0E0E0', borderRadius: 10, textDecoration: 'none', color: '#212121' }}>
                          <span style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
                          <span style={{ fontSize: 12, color: '#757575', whiteSpace: 'nowrap', marginRight: 10 }}>👁 {a.views} · ♡ {a.favorites}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : <p style={{ color: '#9E9E9E', textAlign: 'center', padding: 20 }}>لا توجد إحصائيات</p>}
          </div>
        )}

        {tab === 'reviews' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tabLoading ? <p style={{ color: '#9E9E9E', textAlign: 'center', padding: 20 }}>جاري التحميل...</p> : myReviews.length > 0 ? myReviews.map((r: any) => {
              const incoming = String(r.reviewed_id) === String(user.id);
              const other = incoming ? r.reviewer : r.reviewed;
              return (
                <div key={r.id} style={{ background: '#fff', border: '1px solid #E0E0E0', borderRadius: 12, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#E6F2F0', color: '#005343', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, overflow: 'hidden' }}>
                      {other?.avatar ? <img src={other.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (other?.name?.charAt(0) || '؟')}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{other?.name || 'مستخدم'}</div>
                      <div style={{ fontSize: 11, color: '#9E9E9E' }}>{incoming ? 'قيّمك' : 'قيّمته'}</div>
                    </div>
                    <div style={{ color: '#B8893C', fontSize: 14 }}>{'★'.repeat(Math.round(r.rating || 0))}<span style={{ color: '#E0E0E0' }}>{'★'.repeat(5 - Math.round(r.rating || 0))}</span></div>
                  </div>
                  {r.comment && <p style={{ margin: 0, fontSize: 13, color: '#616161', lineHeight: 1.7 }}>{r.comment}</p>}
                </div>
              );
            }) : <p style={{ color: '#9E9E9E', textAlign: 'center', padding: 20 }}>لا توجد تقييمات بعد</p>}
          </div>
        )}

        {tab === 'blocked' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tabLoading ? <p style={{ color: '#9E9E9E', textAlign: 'center', padding: 20 }}>جاري التحميل...</p> : blockedUsers.length > 0 ? blockedUsers.map((b: any) => {
              const bu = b.blocked || b;
              return (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff', border: '1px solid #E0E0E0', borderRadius: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F5F5F5', color: '#757575', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, overflow: 'hidden' }}>
                    {bu?.avatar ? <img src={bu.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (bu?.name?.charAt(0) || '؟')}
                  </div>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>{bu?.name || 'مستخدم'}</span>
                  <button onClick={async () => {
                    try { await api.delete('/users/' + (bu?.id || b.blocked_id) + '/block'); setBlockedUsers(prev => prev.filter(x => x.id !== b.id)); }
                    catch { /* تجاهل */ }
                  }} style={{ height: 36, padding: '0 14px', background: '#fff', color: '#B91C1C', border: '1.5px solid #FECACA', borderRadius: 8, fontFamily: "'Cairo',sans-serif", fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>إلغاء الحظر</button>
                </div>
              );
            }) : <p style={{ color: '#9E9E9E', textAlign: 'center', padding: 20 }}>لا يوجد مستخدمون محظورون</p>}
          </div>
        )}

        {tab === 'account' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Link to={'/users/' + user.id} style={{ ...btn, background: '#fff', color: '#005343', border: '1.5px solid #005343', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'fit-content' }}>عرض ملفي العام</Link>
            {isSeller ? (
              <>
                <Link to="/my/seller-profile" style={outlineBtn}>بيانات نشاط البائع</Link>
                {accountType !== 'company' && (
                  <Link to="/my/seller-profile" style={{ ...outlineBtn, color: '#B45309', borderColor: '#F59E0B' }}>ترقية إلى حساب شركة</Link>
                )}
              </>
            ) : (
              <div style={{ background: '#E6F2F0', border: '1px solid #BFDEDA', borderRadius: 12, padding: 18 }}>
                <p style={{ margin: '0 0 4px', fontWeight: 800, color: '#003A2F', fontSize: 15 }}>فعّل حساب بائع</p>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: '#4B6B66', lineHeight: 1.7 }}>حسابك حالياً عضو عادي. فعّل حساب بائع (فرد أو شركة) لتتمكن من نشر الإعلانات وعرض ما تقدّمه على الخريطة.</p>
                <Link to="/my/seller-profile" style={{ ...btn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'fit-content' }}>فعّل حساب بائع الآن</Link>
              </div>
            )}
            <button onClick={() => { logout(); navigate('/'); }} style={{ ...btn, background: '#F5F5F5', color: '#424242', width: 'fit-content' }}>تسجيل الخروج</button>

            <div style={{ borderTop: '1px solid #F0F0F0', margin: '8px 0' }} />
            <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 12, padding: 16 }}>
              <p style={{ margin: '0 0 10px', fontWeight: 700, color: '#B45309', fontSize: 14 }}>منطقة الخطر</p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={deactivate} style={{ height: 42, padding: '0 18px', background: '#fff', color: '#B45309', border: '1.5px solid #F59E0B', borderRadius: 10, fontFamily: "'Cairo',sans-serif", fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>تعطيل الحساب مؤقتاً</button>
                <button onClick={deleteAccount} style={{ height: 42, padding: '0 18px', background: '#fff', color: '#B91C1C', border: '1.5px solid #FCA5A5', borderRadius: 10, fontFamily: "'Cairo',sans-serif", fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>حذف الحساب نهائياً</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfilePage;
